import { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { SEO } from '@/components/base/SEO';
import { Navbar } from '@/components/feature/Navbar';
import { Footer } from '@/components/feature/Footer';
import { Breadcrumb } from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';
import { articleImages } from '@/mocks/articleImages';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="px-5 py-2 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: articleImages[article.slug],
    publisher: {
      '@type': 'Organization',
      name: '韩国伦理网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://korean-ethics.com/article/${article.slug}`,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://korean-ethics.com/' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://korean-ethics.com/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      <SEO
        title={`${article.title} - 韩国伦理网`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://korean-ethics.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogUrl={`https://korean-ethics.com/article/${article.slug}`}
      />

      <Navbar />
      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <div className="relative w-full h-[320px] md:h-[480px] rounded-lg overflow-hidden mb-8">
            <img
              src={articleImages[article.slug]}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500 mb-4">
            <span className="bg-accent-100 text-accent-700 px-2.5 py-0.5 rounded-full font-medium">
              {article.categoryName}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i> {article.readTime}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i> {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i> {article.author}
            </span>
          </div>

          <h1 className="text-xl md:text-3xl font-bold font-heading text-foreground-950 mb-6 leading-tight">
            {article.title}
          </h1>

          <div
            className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed mb-10"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap items-center gap-2 mb-12 pt-6 border-t border-background-200">
            <span className="text-sm text-foreground-600 font-medium">标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs cursor-pointer hover:bg-accent-100 hover:text-accent-700 transition-colors duration-200"
              >
                {tag}
              </span>
            ))}
          </div>

          {relatedArticles.length > 0 && (
            <section className="mb-14">
              <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-accent-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-40 overflow-hidden">
                      <img
                        src={articleImages[related.slug]}
                        alt={related.title}
                        title={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold font-heading text-foreground-950 group-hover:text-accent-600 transition-colors duration-200 line-clamp-2 mb-2">
                        {related.title}
                      </h3>
                      <span className="text-xs text-foreground-500">{related.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}